import type { Block } from "@cos/shared";
import type { WidgetState, WidgetMessage, WidgetAttachment } from "./state.js";

export function appendUserMessage(state: WidgetState, clientMsgId: string, text: string, attachments?: WidgetAttachment[]): WidgetState {
  const message: WidgetMessage = { id: clientMsgId, role: "user", text, blocks: null, attachments, pending: true };
  return { ...state, messages: [...state.messages, message], botTyping: true };
}

/**
 * A locally-authored notice, for the failures only the widget can see — an
 * upload that never reached the server, say. Rendered through the ordinary
 * `notice` block so it needs no new UI, but never persisted and never sent:
 * it exists to stop something failing silently in front of the shopper.
 */
export function appendLocalNotice(state: WidgetState, text: string, level: "info" | "warning" | "error" = "error"): WidgetState {
  const block: Block = { type: "notice", level, text };
  const message: WidgetMessage = { id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`, role: "system", text: "", blocks: [block] };
  return { ...state, messages: [...state.messages, message] };
}

/** A delta streams before its message is persisted (no `seq` yet — see `msgDeltaSchema`), so it's addressed by `messageId`. */
export function appendDelta(state: WidgetState, messageId: string, delta: string): WidgetState {
  const index = state.messages.findIndex((m) => m.id === messageId);
  if (index === -1) {
    const message: WidgetMessage = { id: messageId, role: "assistant", text: delta, blocks: null, pending: true };
    return { ...state, messages: [...state.messages, message], botTyping: false };
  }
  const messages = state.messages.slice();
  const existing = messages[index]!;
  messages[index] = { ...existing, text: existing.text + delta };
  return { ...state, messages, botTyping: false };
}

export interface MsgCompleteData {
  messageId: string;
  seq: number;
  /** The served reply in full — present whether or not it streamed. */
  content?: string | null;
  blocks: Block[];
}

/**
 * The completion is authoritative over whatever the deltas built: a reply
 * that never streamed (a provider failure serving the canned message, a
 * budget block) arrives here as its whole text with no message to attach
 * it to, and taking only `blocks` left an empty bubble that filled itself
 * in on the next page load.
 */
export function completeMessage(state: WidgetState, data: MsgCompleteData): WidgetState {
  const index = state.messages.findIndex((m) => m.id === data.messageId);
  const text = data.content ?? "";

  if (index === -1) {
    // Nothing rendered and nothing to render — an intermediate turn the
    // shopper never saw, not a bubble to add.
    if (text.length === 0 && data.blocks.length === 0) return { ...state, lastSeq: Math.max(state.lastSeq, data.seq), botTyping: false };
    const message: WidgetMessage = { id: data.messageId, role: "assistant", text, blocks: data.blocks, seq: data.seq };
    return { ...state, messages: [...state.messages, message], lastSeq: Math.max(state.lastSeq, data.seq), botTyping: false };
  }

  const messages = state.messages.slice();
  const existing = messages[index]!;
  messages[index] = { ...existing, text: text.length > 0 ? text : existing.text, blocks: data.blocks, seq: data.seq, pending: false };
  return { ...state, messages, lastSeq: Math.max(state.lastSeq, data.seq), botTyping: false };
}

/** Used for `msg.new` replay after `resume` — dedupes by id, matching `client_msg_id`/row-id semantics server-side. */
export function upsertMessage(state: WidgetState, message: WidgetMessage): WidgetState {
  const index = state.messages.findIndex((m) => m.id === message.id);
  const lastSeq = Math.max(state.lastSeq, message.seq ?? 0);
  if (index === -1) {
    return { ...state, messages: [...state.messages, message], lastSeq };
  }
  const messages = state.messages.slice();
  messages[index] = message;
  return { ...state, messages, lastSeq };
}

const UNREAD_STORAGE_KEY = "cos_unread";

/**
 * Unread survives page navigation (PLAN.md "persisted in localStorage
 * across page navigations, cleared on open") — a shopper who wanders to
 * another page must still see that someone answered. Storage can be
 * blocked, so both directions degrade to in-memory rather than throwing.
 */
export function readStoredUnread(): { count: number; teaser?: string } {
  try {
    const raw = localStorage.getItem(UNREAD_STORAGE_KEY);
    if (!raw) return { count: 0 };
    const parsed = JSON.parse(raw) as { count?: unknown; teaser?: unknown };
    return {
      count: typeof parsed.count === "number" ? parsed.count : 0,
      teaser: typeof parsed.teaser === "string" ? parsed.teaser : undefined,
    };
  } catch {
    return { count: 0 };
  }
}

export function writeStoredUnread(count: number, teaser?: string): void {
  try {
    if (count === 0) localStorage.removeItem(UNREAD_STORAGE_KEY);
    else localStorage.setItem(UNREAD_STORAGE_KEY, JSON.stringify({ count, teaser }));
  } catch {
    // A shopper in private mode still gets the badge for this page view.
  }
}

/**
 * A message the shopper hasn't seen: it bumps the badge and becomes the
 * teaser only while the panel is closed. With the panel open there is
 * nothing unread — they're looking at it.
 */
export function noteIncoming(state: WidgetState, message: WidgetMessage): WidgetState {
  if (state.panelOpen) return state;
  const unreadCount = state.unreadCount + 1;
  // A reply that is all blocks (products, a form) has no text to preview.
  const teaser = message.text.trim().slice(0, 140) || "Sent you something";
  writeStoredUnread(unreadCount, teaser);
  return { ...state, unreadCount, teaser };
}

export function clearUnread(state: WidgetState): WidgetState {
  writeStoredUnread(0);
  return { ...state, unreadCount: 0, teaser: undefined };
}

/**
 * Unread bookkeeping for a reply that streamed or arrived whole. Only the
 * first sight of a message counts: a `msg.new` replayed after a reconnect,
 * or a `msg.complete` for a reply whose deltas already built the bubble and
 * which was completed once before, must not bump the badge a second time.
 */
export function noteIfFresh(previous: WidgetState, next: WidgetState, messageId: string): WidgetState {
  const before = previous.messages.find((m) => m.id === messageId);
  if (before && !before.pending) return next;
  const message = next.messages.find((m) => m.id === messageId);
  if (!message || (message.role !== "assistant" && message.role !== "agent")) return next;
  if (message.text.trim().length === 0 && (message.blocks?.length ?? 0) === 0) return next;
  return noteIncoming(next, message);
}
